import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import { Button, InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { OtpInputField } from '@xver/components/Molecules';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { getErrorMessage } from '@xver/utils/error';
import { requestAuthUser } from '@xver/store/auth/auth.actions';

const PHONE_REGEX = /^0[0-9]{9}$/;

interface ChangePhoneSectionProps {
    user: User;
}

export default function ChangePhoneSection({ user }: ChangePhoneSectionProps) {
    const { t } = useTranslation('security');
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    const [stage, setStage] = useState<'idle' | 'enter-otp'>('idle');
    const [newPhone, setNewPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [otpResetKey, setOtpResetKey] = useState(0);

    const validation = useMemo(() => {
        if (!newPhone) return t('errors.phoneInvalid');
        if (!PHONE_REGEX.test(newPhone)) return t('errors.phoneInvalid');
        if (newPhone === user.phoneNumber) return t('errors.phoneSame');
        return undefined;
    }, [newPhone, user.phoneNumber, t]);

    const requestMutation = useMutation({
        mutationFn: () => Endpoints.v1.Users.sendChangePhone({ phoneNumber: newPhone }),
        onSuccess: () => {
            notifications.show({
                title: t('toast.codeSentTitle'),
                message: t('toast.codeSentMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            setStage('enter-otp');
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const confirmMutation = useMutation({
        mutationFn: () => Endpoints.v1.Users.verifyChangePhone({ otpCode: otp }),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.phoneChangedTitle'),
                message: t('toast.phoneChangedMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            dispatch(requestAuthUser());
            setStage('idle');
            setNewPhone('');
            setOtp('');
            setSubmitAttempted(false);
            setOtpResetKey((k) => k + 1);
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleRequest = () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (validation) return;
        requestMutation.mutate();
    };

    const handleConfirm = () => {
        if (otp.length !== 6) return;
        confirmMutation.mutate();
    };

    return (
        <DialogBox
            label={t('phone.title')}
            description={t('phone.description')}
            classNames="w-full flex flex-col justify-start items-center p-4 gap-4"
        >
            <div className="w-full flex flex-row justify-between items-center">
                {stage === 'idle' ? (
                    <div className="w-full flex flex-row gap-4">
                        <InputField
                            size="md"
                            label={t('phone.current')}
                            value={user.phoneNumber}
                            disabled
                        />
                        <InputField
                            size="md"
                            label={t('phone.newPhone')}
                            placeholder={t('phone.newPhonePlaceholder')}
                            value={newPhone}
                            onChange={(e) =>
                                setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
                            }
                            error={submitAttempted ? validation : undefined}
                        />
                    </div>
                ) : (
                    <div className="w-full flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <span className="text-sm mb-2">{t('phone.otp')}</span>
                            <OtpInputField
                                length={6}
                                onComplete={setOtp}
                                onChange={setOtp}
                                resetKey={otpResetKey}
                            />
                        </div>
                        <div className="flex justify-start">
                            <Button onClick={handleConfirm} isLoading={confirmMutation.isPending}>
                                {t('phone.submit')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            {stage === 'idle' && (
                <div className="w-full h-full flex justify-end items-center">
                    <Button onClick={handleRequest} isLoading={requestMutation.isPending}>
                        {t('phone.requestCode')}
                    </Button>
                </div>
            )}
        </DialogBox>
    );
}
